/* eslint-disable @typescript-eslint/no-unused-vars */
import {
	Dialog,
	Paper,
	DialogTitle,
	DialogContent,
	Box,
	Typography,
	TextField,
	DialogActions,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	FormControl,
	SxProps,
	Theme,
	FormControlLabelProps,
	styled,
} from '@mui/material'
import { useRadioGroup } from '@mui/material/RadioGroup'
import { useSelector } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import { AvailabilityModalData } from '../../../../../types/modal'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { TimesheetStoreState } from '../../../../../store/timesheetStore'

interface StyledFormControlLabelProps extends FormControlLabelProps {
	checked: boolean
}

/**
 * Custom styles object for the `CustomModalAvailability` component.
 * Defines the styling for various elements within the component using the `SxProps<Theme>` type.
 *
 * @property {SxProps<Theme>} title - Styles for the title element, including font weight, size, family, and color.
 * @property {SxProps<Theme>} area_title - Styles for the area title, including font weight, size, family, color, line height, and letter spacing.
 * @property {SxProps<Theme>} subtitle - Styles for the subtitle element, including font weight, size, family, and color.
 * @property {SxProps<Theme>} area_label - Styles for the area label, including font weight, size, family, color, and line height.
 * @property {SxProps<Theme>} area_text - Styles for the area text, including font weight, size, family, font style, color, and line height.
 * @property {SxProps<Theme>} radio_label - Styles for the radio label, including font weight, size, family, font style, color, and line height.
 * @property {SxProps<Theme>} fields - Styles for the fields, including width, font weight, family, size, color, line height, padding, border radius, border, background, and outline.
 * - Includes nested styles for:
 *   - `& input`: Styles for the input element, including font family, color, padding, font size, and font weight.
 *   - `& .MuiOutlinedInput-notchedOutline`: Styles for the outlined input's notched outline in various states (default, focus, hover, active), ensuring no border or outline is applied.
 */
const customeStyles: {
	title: SxProps<Theme>
	area_title: SxProps<Theme>
	subtitle: SxProps<Theme>
	area_label: SxProps<Theme>
	area_text: SxProps<Theme>
	radio_label: SxProps<Theme>
	fields: SxProps<Theme>
} = {
	title: {
		fontWeight: 600,
		fontSize: '1rem',
		fontFamily: 'var(--font-family)',
		color: 'var(--primary-dark)',
	},
	subtitle: {
		fontWeight: 600,
		fontSize: '14px',
		fontFamily: 'var(--font-family)',
		color: 'var(--primary-dark)',
	},
	area_title: {
		fontWeight: 600,
		fontSize: '12px',
		fontFamily: 'var(--font-family)',
		color: 'var(--primary-light)',
		lineHeight: '24px',
		letterSpacing: '0.17px',
	},
	area_label: {
		fontWeight: 400,
		fontSize: '12px',
		fontFamily: 'var(--font-family)',
		color: 'var(--primary-dark)',
		lineHeight: '130%',
	},
	area_text: {
		fontWeight: 400,
		fontSize: '12px',
		fontFamily: 'var(--font-family)',
		fontStyle: 'normal',
		color: 'var(--primary-light)',
		lineHeight: '130%',
	},
	radio_label: {
		fontWeight: 500,
		fontSize: '13px',
		fontFamily: 'var(--font-family)',
		fontStyle: 'normal',
		color: 'var(--primary-dark)',
		lineHeight: '130%',
	},
	fields: {
		width: '108px',
		fontWeight: 500,
		fontFamily: 'var(--font-family)',
		fontSize: '13px',
		color: 'var(--primary-dark)',
		lineHeight: '1.3',
		padding: 0,
		borderRadius: '4px',
		border: '1px solid rgba(134, 149, 175, 0)',
		background: 'var(--secondary-light)',
		outline: 'none',
		'& input': {
			fontFamily: 'var(--font-family)',
			color: 'var(--primary-dark)',
			padding: '12px',
			fontSize: '13px',
			fontWeight: 500,
		},
		'& .MuiOutlinedInput-notchedOutline': {
			outline: 'none',
			border: 'none',
		},
		'& .MuiOutlinedInput-notchedOutline:focus': {
			outline: 'none',
			border: 'none',
		},
		'& .MuiOutlinedInput-notchedOutline:hover': {
			outline: 'none',
			border: 'none',
		},
		'& .MuiOutlinedInput-notchedOutline:active': {
			outline: 'none',
			border: 'none',
		},
	},
}

/**
 * A styled version of the `FormControlLabel` component that applies custom styles
 * based on the `checked` prop. This component uses the `styled` utility from
 * Material-UI to define conditional styles for the label.
 *
 * - @param props - The props for the `StyledFormControlLabel` component.
 * - @returns A styled `FormControlLabel` component with conditional styles.
 *
 * - @example
 * ```tsx
 * <StyledFormControlLabel
 *   control={<Radio />}
 *   label="Option 1"
 *   checked={true}
 * />
 * ```
 */
const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => <FormControlLabel {...props} />)(
	({ theme }) => ({
		variants: [
			{
				props: { checked: true },
				style: {
					'.MuiFormControlLabel-label': {
						...customeStyles.radio_label,
					},
				},
			},
			{
				props: { checked: false },
				style: {
					'.MuiFormControlLabel-label': {
						...customeStyles.radio_label,
					},
				},
			},
		],
	}),
)

/**
 * A custom implementation of the `FormControlLabel` component that integrates
 * with the `RadioGroup` context to determine whether the label is checked.
 *
 * - @param props - The props for the `MyFormControlLabel` component.
 * - @returns A `StyledFormControlLabel` component with the `checked` state.
 *
 * - @example
 * ```tsx
 * <MyFormControlLabel
 *   value="option1"
 *   control={<Radio />}
 *   label="Option 1"
 * />
 * ```
 */
function MyFormControlLabel(props: FormControlLabelProps) {
	const radioGroup = useRadioGroup()

	let checked = false

	if (radioGroup) {
		checked = radioGroup.value === props.value
	}

	return (
		<StyledFormControlLabel
			checked={checked}
			{...props}
		/>
	)
}

const drivingBreakOptions: {
	value: number
	title: string
	englishTitle: string
}[] = [
	{
		value: 1,
		title: '2 Pausas de 15/30 min',
		englishTitle: 'TwoBreaks15And30',
	},
	{ value: 0, title: 'Pausa de 45 min', englishTitle: 'Single45' },
	{ value: 2, title: 'Sem Pausa', englishTitle: 'None' },
]

export function getDrivingBreakValue(drivingBreak: string): number | null {
	console.log('drivingBreak getDrivingBreakValue', drivingBreak)
	return drivingBreakOptions.find(option => option.englishTitle === drivingBreak)?.value ?? null
}

/**
 *  ## CustomModalAvailability component
 *
 * - @param {CustomModalAvailabilityProps} props - Component props
 * - @returns {JSX.Element} The rendered CustomModalAvailability component
 */
const CustomModalAvailability: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers, modalTitle } = useModal()
	// The Redux-dispatching handler (the second element of `modalHandlers`) is intentionally not destructured/called
	// directly from this component anymore — see `selectedDrivingBreak` below for why.
	const [saveChanges] = modalHandlers
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)

	const {
		drivingBreak,
		overtime,
		totalAvailability,
		totalAvailabilityFormatted,
		totalsByPeriod,
		workedSchedule,
		isSavingAvailability,
	} = modalData as AvailabilityModalData

	const committedDrivingBreakValue = useMemo(() => {
		return timesheetPage?.dispDrivingBreak ?? getDrivingBreakValue(drivingBreak ?? '')
	}, [timesheetPage?.dispDrivingBreak, drivingBreak])

	// The radio selection is held locally and only committed to Redux (via `handleDrivingBreak`, called from
	// `saveChanges`) when the user clicks "Guardar Alterações". This is what makes "Cancelar" safe: since
	// clicking it just closes the modal without ever touching Redux, `TimesheetPage.dispDrivingBreak` is left
	// exactly as it was before the modal opened. Re-synced from the committed value every time the modal
	// transitions from closed to open, so a fresh open always starts from the current committed selection
	// rather than a value left over from a previous cancel.
	const [selectedDrivingBreak, setSelectedDrivingBreak] = useState<number | null>(committedDrivingBreakValue)

	useEffect(() => {
		if (isOpen) {
			setSelectedDrivingBreak(committedDrivingBreakValue)
		}
		// Deliberately only reacts to the closed->open transition, not to every change of committedDrivingBreakValue,
		// so it doesn't stomp on the user's in-progress selection while the modal remains open.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	if (!isOpen || !modalData || Object.keys(modalData).length === 0 || !modalHandlers || modalHandlers.length < 2) {
		return null
	}

	const handleSave = () => {
		if (selectedDrivingBreak === null) return
		saveChanges(selectedDrivingBreak)
	}

	return (
		<Dialog
			sx={{ minWidth: '600px' }}
			open={isOpen}
		>
			<Paper sx={{ minWidth: '600px' }}>
				<DialogTitle
					id='error-modal-title'
					variant='h6'
					component='h2'
					sx={{ ...customeStyles.title }}
				>
					{modalTitle}
				</DialogTitle>
				<DialogContent
					sx={{
						display: 'grid',
						gap: '1rem',
						paddingBlock: '0 1.5rem',
						paddingInline: '1.5rem',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
							gap: '1rem',
						}}
					>
						<Typography sx={{ ...customeStyles.subtitle }}>Disponibilidade</Typography>
						<Box
							sx={{
								width: '100%',
								display: 'grid',
								gridTemplateColumns: 'repeat(5, 1fr)',
							}}
						>
							<Typography
								sx={{
									padding: '1rem',
									gridColumnStart: 1,
									gridColumnEnd: -1,
									width: '100%',
									textAlign: 'center',
									placementSelf: 'center',
									borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
									...customeStyles.area_title,
								}}
							>
								Horário Trabalhado
							</Typography>
							{workedSchedule?.map(period => (
								<Box
									key={period.startTime}
									sx={{
										padding: '1rem',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										justifyContent: 'center',
										placementSelf: 'center',
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-start',
										}}
									>
										<Typography sx={{ ...customeStyles.area_label }}>{period.startTime}</Typography>
										<Typography sx={{ ...customeStyles.area_label }}>{' - '}</Typography>
										<Typography sx={{ ...customeStyles.area_label }}>{period.endTime}</Typography>
									</Box>
									<Typography sx={{ ...customeStyles.area_text }}>{`(${period.duration})`}</Typography>
								</Box>
							))}
						</Box>
						<Box
							sx={{
								width: '100%',
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
							}}
						>
							<Typography
								sx={{
									padding: '1rem',
									gridColumnStart: 1,
									gridColumnEnd: -1,
									width: '100%',
									textAlign: 'center',
									borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
									...customeStyles.area_title,
								}}
							>
								Totais por Período
							</Typography>
							<Box sx={{ padding: '1rem' }}>
								<Typography sx={{ ...customeStyles.area_label }}>Noturno Manhã</Typography>
								<Typography sx={{ ...customeStyles.area_text }}>das 03:00 às 07:00</Typography>
							</Box>
							<Box sx={{ padding: '1rem' }}>
								<Typography sx={{ ...customeStyles.area_label }}>Diurno</Typography>
								<Typography sx={{ ...customeStyles.area_text }}>das 07:00 às 20:00</Typography>
							</Box>
							<Box sx={{ padding: '1rem' }}>
								<Typography sx={{ ...customeStyles.area_label }}>Noturno Tarde</Typography>
								<Typography sx={{ ...customeStyles.area_text }}>das 20:00 às 03:00</Typography>
							</Box>
							<Box sx={{ padding: '1rem' }}>
								<Typography
									aria-label='total-night-morning'
									sx={{ ...customeStyles.area_label }}
								>
									{totalsByPeriod?.nightMorning.duration}
								</Typography>
							</Box>
							<Box sx={{ padding: '1rem' }}>
								<Typography
									aria-label='total-day-time'
									sx={{ ...customeStyles.area_label }}
								>
									{totalsByPeriod?.daytime.duration}
								</Typography>
							</Box>
							<Box sx={{ padding: '1rem' }}>
								<Typography
									aria-label='total-night-evening'
									sx={{ ...customeStyles.area_label }}
								>
									{totalsByPeriod?.nightEvening.duration}
								</Typography>
							</Box>
						</Box>
					</Box>
					<Box
						sx={{
							marginTop: '1.5rem',
							display: 'grid',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '1rem',
								}}
							>
								<Typography sx={{ ...customeStyles.subtitle }}>Disponibilidade Total</Typography>
								<TextField
									size='small'
									label='Total Horas'
									value={totalAvailabilityFormatted}
									InputProps={{ readOnly: true }}
									sx={{ ...customeStyles.fields }}
								/>
							</Box>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '1rem',
								}}
							>
								<Typography sx={{ ...customeStyles.subtitle }}>Trabalho Extra</Typography>
								<TextField
									size='small'
									label='Horas Extras'
									value={overtime}
									InputProps={{ readOnly: true }}
									sx={{ ...customeStyles.fields }}
								/>
							</Box>
						</Box>
						<FormControl
							sx={{ marginTop: '1.5rem' }}
						>
							<FormLabel
								htmlFor='driving-break'
								sx={{
									...customeStyles.subtitle,
									'&.Mui-focused': {
										color: 'inherit', // Prevent color change on focus
									},
								}}
							>
								Pausa de Condução
							</FormLabel>
							<RadioGroup
								row
								id='driving-break'
								aria-describedby='escolha os tipos de pausa'
								value={selectedDrivingBreak}
								onChange={e => setSelectedDrivingBreak(Number(e.target.value))}
							>
								{drivingBreakOptions?.map(option => (
									<MyFormControlLabel
										key={option.value + option.englishTitle}
										value={option.value}
										control={<Radio />}
										label={option.title}
									/>
								))}
							</RadioGroup>
						</FormControl>
					</Box>
				</DialogContent>
				<DialogActions
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '0.5rem',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal} // Call closeModal when Fechar is clicked; the local selection is discarded, Redux is untouched
						disabled={isSavingAvailability}
						sx={{ marginLeft: 'auto' }}
					>
						Cancelar
					</CustomButton>
					<CustomButton
						onClick={handleSave}
						isLoading={isSavingAvailability}
						sx={{ marginLeft: 'auto' }}
					>
						Guardar Alterações
					</CustomButton>
				</DialogActions>
			</Paper>
		</Dialog>
	)
}

export default CustomModalAvailability
